import {useEffect, useState} from 'react';
import type { CalendarProps } from 'antd';
import type { Dayjs } from 'dayjs';
import {useNavigate} from 'react-router-dom';
import {LeftOutlined, RightOutlined} from '@ant-design/icons';
import {Button, Calendar, ConfigProvider, Divider, Drawer, Empty, Flex, Image, Segmented, Select, Skeleton, Typography} from 'antd';
import localeCalendarioPtBr from 'antd/es/calendar/locale/pt_BR';
import {AGENDAMENTO_MESSAGE, PRE_AGENDAMENTO_MESSAGE, ROLES} from '@constants';
import { useAppMessage } from '@providers'
import {useFiliaisQuery} from '@queries';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import './style.scss';
import {useGlobalFilial, useGlobalUser, useVolumesFilial} from "@hooks";
import {calcularDatasDesabilitadas, criarFuncaoDataDesabilitada, resolverMensagemErro} from "@utils";
import type { Identificador } from '../../types';

const {Text, Title} = Typography;

type ModoCalendario = NonNullable<CalendarProps<Dayjs>['mode']>;

const OPCOES_MODO_CALENDARIO = [
    {label: 'Mês', value: 'month'},
    {label: 'Ano', value: 'year'},
] satisfies { label: string; value: ModoCalendario }[];

const DIAS_SEMANA_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

const LOCALE_CALENDARIO: CalendarProps<Dayjs>['locale'] = {
    ...localeCalendarioPtBr,
    lang: {...localeCalendarioPtBr.lang, shortWeekDays: DIAS_SEMANA_CURTOS},
};

const TEMA_SELETOR_MODO = {
    components: {
        Segmented: {
            itemSelectedBg: 'var(--cor-primaria)',
            itemSelectedColor: 'var(--cor-texto-branco)',
            trackBg: 'transparent',
        },
    },
};

const resolverMotivoBloqueio = (data: Dayjs, hoje: Dayjs): string | null => {
    const isDataPassada = data.isBefore(hoje, 'day');
    const isDomingo = data.day() === 0;
    const isSabado = data.day() === 6;

    if (isDataPassada) return 'Data passada';
    if (isDomingo) return 'Domingo';
    if (isSabado) return 'Sábado';
    return null;
};

export const Home = () => {
    const message = useAppMessage();
    const navigate = useNavigate();
    const [user] = useGlobalUser();
    const [selectedFilial, setSelectedFilial] = useGlobalFilial();
    const [viewDate, setViewDate] = useState(dayjs());
    const [navigateDate, setNavigateDate] = useState('');
    const [viewMode, setViewMode] = useState<CalendarProps<Dayjs>['mode']>('month');
    const [openScheduleModal, setOpenScheduleModal] = useState(false);
    const [visible, setVisible] = useState(true);

    const {data: filialData = [], error: erroFiliais} = useFiliaisQuery();
    const {eventsData, volumeFinalGeral, isLoading: loadingCalendar, erroVolumes, erroVolumeGeral} =
        useVolumesFilial(selectedFilial);

    useEffect(() => {
        if (erroFiliais) message.error(resolverMensagemErro(erroFiliais, "Erro ao buscar as Filias!!"));
    }, [erroFiliais]);

    useEffect(() => {
        if (erroVolumes) message.error(resolverMensagemErro(erroVolumes, "Erro ao buscar volumes!!"));
    }, [erroVolumes]);

    useEffect(() => {
        if (erroVolumeGeral) message.error(resolverMensagemErro(erroVolumeGeral, "Erro ao buscar volumes!!"));
    }, [erroVolumeGeral]);

    const handleFilialChange = (value: Identificador) => {
        setSelectedFilial(String(value));
    };

    const onCloseScheduleModal = () => {
        setOpenScheduleModal(false);
    };

    const onSelect: CalendarProps<Dayjs>['onSelect'] = (value, mode) => {
        setViewDate(value);

        if (mode.source === "date") {
            const dataSelecionada = value.format('DD-MM-YYYY');
            setNavigateDate(dataSelecionada);
            const temPerfilPreAgendamento = user.userRoles.some(role => ROLES.PRE_AGENDAMENTO.includes(role));
            if (temPerfilPreAgendamento) {
                setOpenScheduleModal(true);
            } else {
                navigate(`/agendamento/${dataSelecionada}`);
            }
        }

        if (mode.source === "month") {
            setViewMode('month');
        }
    };

    const onPanelChange: CalendarProps<Dayjs>['onPanelChange'] = (_value, mode) => {
        setViewMode(mode);
        setViewDate(dayjs());
    };

    const buscarEventoDoDia = (value: Dayjs) => {
        const dataFormatada = value.format('YYYY-MM-DD');
        return eventsData.find(event => event.date === dataFormatada);
    };

    const renderDia = (data: Dayjs, hoje: Dayjs) => {
        const isForaDoMes = !data.isSame(viewDate, 'month');
        const isHoje = data.isSame(hoje, 'day');
        const evento = buscarEventoDoDia(data);
        const motivoBloqueio = resolverMotivoBloqueio(data, hoje);
        const classeNumero = isHoje
            ? 'calendario-dia-numero-class calendario-dia-numero-hoje-class'
            : 'calendario-dia-numero-class';

        if (isForaDoMes) {
            return (
                <div className="calendario-dia-class">
                    <span className="calendario-dia-numero-class">{data.date()}</span>
                </div>
            );
        }

        return (
            <div className="calendario-dia-class">
                <span className="calendario-dia-topo-class">
                    <span className={classeNumero}>{data.date()}</span>
                    {isHoje && <span className="calendario-dia-hoje-rotulo-class">Hoje</span>}
                </span>
                <span className="calendario-dia-rodape-class">
                    {evento && (
                        <span className="calendario-dia-volume-class">
                            <span className="calendario-dia-volume-ponto-class" aria-hidden="true"/>
                            T.Volumes: {evento.totalVolumes}
                        </span>
                    )}
                    {motivoBloqueio && <span className="calendario-dia-motivo-class">{motivoBloqueio}</span>}
                </span>
            </div>
        );
    };

    const fullCellRender: CalendarProps<Dayjs>['fullCellRender'] = (current, info) => {
        if (info.type === 'date') return renderDia(current, info.today);
        return info.originNode;
    };

    const headerRender: CalendarProps<Dayjs>['headerRender'] = ({value, type, onChange, onTypeChange}) => {
        const isModoMes = type === 'month';
        const tituloPeriodo = isModoMes
            ? value.locale('pt-br').format('MMMM [de] YYYY')
            : value.format('YYYY');
        const unidadeNavegacao: ModoCalendario = isModoMes ? 'month' : 'year';
        const rotuloAnterior = isModoMes ? 'Mês anterior' : 'Ano anterior';
        const rotuloProximo = isModoMes ? 'Próximo mês' : 'Próximo ano';

        return (
            <div className="calendar-head-class">
                <Title level={2} className="calendar-head-periodo-class">{tituloPeriodo}</Title>
                <div className="calendar-head-nav-class">
                    <Button
                        icon={<LeftOutlined/>}
                        aria-label={rotuloAnterior}
                        onClick={() => onChange(value.subtract(1, unidadeNavegacao))}
                    />
                    <Button
                        icon={<RightOutlined/>}
                        aria-label={rotuloProximo}
                        onClick={() => onChange(value.add(1, unidadeNavegacao))}
                    />
                    <ConfigProvider theme={TEMA_SELETOR_MODO}>
                        <Segmented<ModoCalendario>
                            aria-label="Modo de visualização"
                            options={OPCOES_MODO_CALENDARIO}
                            value={type}
                            onChange={onTypeChange}
                        />
                    </ConfigProvider>
                </div>
            </div>
        );
    };

    const datasDesabilitadas = calcularDatasDesabilitadas();
    const disabledDate = criarFuncaoDataDesabilitada(eventsData, datasDesabilitadas);

    const semFilialSelecionada = filialData.length === 0 && !loadingCalendar;
    const filialSelecionada = filialData.find(filial => String(filial.value) === selectedFilial);
    const valorSelectFilial = filialSelecionada?.value ?? null;

    return (
        <>
            <Flex vertical={true} className={'ant-flex-vertical-base'}>
                <Image
                    className={'alert-image-style'}
                    preview={{
                        visible,
                        maskClassName: 'alert-mask-image-style',
                        mask: null,
                        src: 'https://cdn.diferpan.com.br/arquivos/publico/sgd/08_Tabela_Descarga_sem_moldura.png',
                        onOpenChange: (value) => {
                            setVisible(value);
                        }
                    }}
                />
                <Skeleton loading={loadingCalendar} active paragraph={{rows: 20}} className="calendar-skeleton-class">

                    {semFilialSelecionada ? (
                        <Empty description="Nenhuma filial disponivel. Verifique sua permissao de acesso." />
                    ) : (
                        <>
                            <div className={"calendar-toolbar-class"}>
                                <div className={"calendar-toolbar-filial-class"}>
                                    <Text strong className="calendar-toolbar-label-class">Filial</Text>
                                    <Select
                                        value={valorSelectFilial}
                                        placeholder="Selecione a filial"
                                        options={filialData}
                                        onChange={handleFilialChange}
                                        aria-label="Selecionar filial"
                                        className="select-filial-class"
                                    />
                                </div>

                                <ul className={"calendar-toolbar-legend-class"} aria-label="Legenda de disponibilidade">
                                    <li className={"calendar-legend-chip-class"}>
                                        <span
                                            className={"calendar-legend-dot-class calendar-legend-dot-liberado-class"}
                                            aria-hidden="true"
                                        />
                                        Liberado — abaixo de <strong>{volumeFinalGeral}</strong> volumes
                                    </li>
                                    <li className={"calendar-legend-chip-class"}>
                                        <span
                                            className={"calendar-legend-dot-class calendar-legend-dot-bloqueado-class"}
                                            aria-hidden="true"
                                        />
                                        Bloqueado — acima de <strong>{volumeFinalGeral}</strong> volumes
                                    </li>
                                </ul>
                            </div>

                            <div className="calendar-region-class">
                                <section className="calendar-card-class" aria-label="Calendário de agendamentos">
                                    <Calendar
                                        value={viewDate}
                                        mode={viewMode}
                                        onPanelChange={onPanelChange}
                                        onSelect={onSelect}
                                        headerRender={headerRender}
                                        fullCellRender={fullCellRender}
                                        locale={LOCALE_CALENDARIO}
                                        disabledDate={disabledDate}
                                        className="calendar-scheduler-home-class"
                                    />
                                </section>
                            </div>
                        </>
                    )}
                </Skeleton>
            </Flex>
            <Drawer
                title="Como Deseja Prosseguir?"
                placement="top"
                closable={true}
                onClose={onCloseScheduleModal}
                open={openScheduleModal}
                height="55%"
            >
                <Flex vertical={true} gap="small" justify={'center'} align={'center'}>
                    <Title level={5}><Text strong>Agendamento</Text></Title>
                    <Text>{AGENDAMENTO_MESSAGE}</Text>

                    <Button type="primary" onClick={() => navigate(`/agendamento/${navigateDate}`)}>Agendamento</Button>

                    <Divider/>

                    <Title level={5}><Text strong>Pre-Agendamento</Text></Title>
                    <Text>{PRE_AGENDAMENTO_MESSAGE}</Text>

                    <Button type="primary" onClick={() => navigate(`/agendamento/pre/${navigateDate}`)}>
                        Pre - Agendamento
                    </Button>
                </Flex>
            </Drawer>
        </>
    );
};
